<template>
  <mi-form class="new-bom-validation-form" @submit="handleSubmit">
    <div class="row new-bom-validation-form__inputs">
      <div class="col-8 q-pr-lg q-pb-md new-bom-validation-form__col">
        <mi-text-field
          v-model="validationForm.componentSequence"
          :rules="inputValidationRules"
          class="q-mb-sm"
          label="Component IDs"
          placeholder="e.g. 00TL;005L"
          outlined
        ></mi-text-field>
      </div>
      <div class="col-4 new-bom-validation-form__col">
        <mi-text-field
          v-model="validationForm.planningPeriod"
          :rules="[customPlanningPeriodValidationRules]"
          :class="isPlanningPeriodEmpty && 'new-bom-validation-form__inputs-mask'"
          label="Planning period"
          mask="###### - ######"
          fill-mask="_"
          outlined
          prefix="PP"
        >
        </mi-text-field>
      </div>
    </div>
    <div class="new-bom-validation-form__actions">
      <mi-btn flat @click="closeDialog"> Cancel </mi-btn>
      <mi-btn type="submit" :loading="isCreateValidationLoading">
        <span> Create validation </span>
      </mi-btn>
    </div>
  </mi-form>
</template>

<script>
  import { createNamespacedHelpers } from 'vuex'
  import { minLength, required } from '@/utils/validators'
  import { MIN_LENGTH_ERROR } from '@/utils/validators/errorMessages'
  import { createOnDemandExportsBomValidation } from '@/api'
  import { removePlanningPeriodMask, addPrefixToPlanningPeriod } from '@/utils/planningPeriod'
  import notify from '@/utils/notify'
  import recordAnalytics from '@/utils/analytics/recordAnalytics'
  import { ON_DEMAND_EXPORTS_BOM_VALIDATION_CREATED } from '@/utils/analytics/constants'

  const { mapGetters, mapActions } = createNamespacedHelpers('rule-validation')

  export default {
    name: 'NewBomValidationForm',
    props: {
      validationType: {
        type: Object,
        required: true
      }
    },
    emits: ['update:model-value'],
    data: () => ({
      isCreateValidationLoading: false,
      validationForm: {
        componentSequence: '',
        planningPeriod: ''
      },
      inputValidationRules: [
        required,
        minLength(4),
        // must have ; and
        v => {
          const componentListLength = v.split(';').length
          if (!v.includes(';') && componentListLength > 1) return 'Component IDs must be separated by ;'
          return true
        },
        // ; cannot be the last input
        v => {
          if (v.endsWith(';')) return '";" cannot be the last input'
          return true
        },
        // only allow numbers and letters
        v => {
          if (!/^[0-9a-zA-Z;]+$/.test(v)) return 'Only uppercase letters, numbers are allowed'
          return true
        },
        // the number of components must be less than 20
        v => {
          const componentList = v.split(';')
          if (componentList.length > 20) return 'You can only validate up to 20 components at a time'
          return true
        }
      ]
    }),
    computed: {
      ...mapGetters([
        'getSelectedUserExport'
      ]),
      isPlanningPeriodEmpty() {
        const hasNumber = /\d/
        return !hasNumber.test(this.validationForm.planningPeriod)
      }
    },
    methods: {
      ...mapActions(['fetchValidations']),

      customPlanningPeriodValidationRules() {
        const PLANNING_PERIOD_LENGTH = 12
        if (this.isPlanningPeriodEmpty) return required()
        if (!this.isPlanningPeriodEmpty && this.validationForm.planningPeriod.includes('_')) {
          return MIN_LENGTH_ERROR.replace('{length}', PLANNING_PERIOD_LENGTH)
        }
        return true
      },
      closeDialog() {
        this.$emit('update:model-value')
      },
      async handleSubmit() {
        this.isCreateValidationLoading = true
        try {
          const { initialPP, finalPP } = removePlanningPeriodMask(this.validationForm.planningPeriod)
          const { componentSequence } = this.validationForm
          const formattedComponentSequence = componentSequence.toUpperCase().split(' ').join('')
          const componentList = [...new Set(formattedComponentSequence.split(';'))]

          const result = await createOnDemandExportsBomValidation({
            ...this.validationForm,
            componentList,
            initialPP: addPrefixToPlanningPeriod(initialPP),
            finalPP: addPrefixToPlanningPeriod(finalPP),
            userExportID: this.getSelectedUserExport.id
          })

          const failedComponents = this.getFailedComponents(result)

          if (!failedComponents.length) {
            notify({
              title: 'COMPLETED',
              content: `${ componentList.length } component(s) were successfully requested.`,
              type: 'dark',
              closeIcon: false
            })
            this.closeDialog()
            this.fetchValidations()
            recordAnalytics(ON_DEMAND_EXPORTS_BOM_VALIDATION_CREATED)
          }
          else {
            notify({
              title: 'PARTIALLY COMPLETED',
              content: 'Change the remaining components. They are not found in this product model.',
              type: 'dark_no_icon',
              position: 'center',
              closeIcon: false
            })
            this.validationForm.componentSequence = failedComponents.join(';')
          }
        }
        catch (err) {
          throw new Error('An error occurred trying to submit a new BOM validation', err)
        }
        finally {
          this.isCreateValidationLoading = false
        }
      },
      getFailedComponents(data) {
        return data
          .filter(component => component.status === 'FAILED')
          .map(component => component.componentId)
      }
    }
  }
</script>

<style lang="scss">
  .new-bom-validation-form {
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: space-between;

    &__inputs {
      padding: 36px 40px;

      &-mask {
        .q-field__prefix,
        input {
          font-size: .875rem;
          color: $mi-anthracite-400;
        }
      }
    }

    &__actions {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      flex-direction: row;
      width: 100%;
      gap: 16px;
      height: 84px;
      padding: 24px;
      box-shadow: 0 8px 16px rgba(0, 0, 0, .24);
    }
  }

  .q-notification.mi-notification--dark_no_icon {
    max-width: 924px;
    width: 50%;
    margin-bottom: 22.5%;
  }

  @media (max-width: $mi-responsive-medium-width) {
    .new-bom-validation-form {
      &__inputs {
        flex-direction: column;
      }

      &__col {
        // stylelint-disable declaration-no-important
        width: 100% !important;
        padding-right: 0;
      }
    }
  }
</style>
